import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { queryClient } from '../queryClient';

export type MaintenanceReportStatus = 'submitted' | 'in_progress' | 'resolved' | 'closed';

export interface MaintenanceReport {
  id: string;
  reporter_id?: string | null;
  reporter_name: string;
  reporter_email?: string | null;
  reporter_phone?: string | null;
  title: string;
  location: string;
  description: string;
  status: MaintenanceReportStatus;
  admin_notes?: string | null;
  source: 'mobile' | 'website' | 'webapp';
  notification_group_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MaintenanceReportingSettings {
  id: number;
  notification_group_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export const useMaintenanceReports = () =>
  useQuery<MaintenanceReport[]>({
    queryKey: ['maintenance-reports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('maintenance_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as MaintenanceReport[]) ?? [];
    },
    staleTime: 60_000,
  });

export const useUpdateMaintenanceReport = () =>
  useMutation({
    mutationFn: async (payload: {
      id: string;
      status: MaintenanceReportStatus;
      admin_notes?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('maintenance_reports')
        .update({
          status: payload.status,
          admin_notes: payload.admin_notes ?? null,
        })
        .eq('id', payload.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as MaintenanceReport;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
    },
  });

export const useMaintenanceReportingSettings = () =>
  useQuery<MaintenanceReportingSettings | null>({
    queryKey: ['maintenance-reporting-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('maintenance_reporting_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return (data as MaintenanceReportingSettings | null) ?? null;
    },
  });

export const useUpdateMaintenanceReportingSettings = () =>
  useMutation({
    mutationFn: async (payload: { notification_group_id?: string | null }) => {
      const { data, error } = await supabase
        .from('maintenance_reporting_settings')
        .upsert(
          {
            id: 1,
            notification_group_id: payload.notification_group_id ?? null,
          },
          { onConflict: 'id' },
        )
        .select('*')
        .single();
      if (error) throw error;
      return data as MaintenanceReportingSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-reporting-settings'] });
    },
  });
